import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { listDepartments } from '@/data/departments'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { MOVEMENT_STATUS_LABELS } from '@/data/remittances'
import type { MovementStatus } from '@/data/remittances'
import { getMonthStatement, getQuarterReport } from '@/data/reports'
import { downloadCsv } from '@/lib/csv'
import { formatDay, formatMonth } from '@/lib/format'
import { formatKwacha } from '@/lib/money'
import { currentQuarterId, lusakaToday, monthId } from '@/lib/periods'
import { percent, recentMonths, recentQuarters } from '@/lib/reportMath'
import { monthStatementRows, quarterReportRows } from '@/lib/reportCsv'
import { quarterLabel } from '@/features/budgets/schemas'
import { MonthStatementView } from './MonthStatementView'
import { PeriodSwitch } from './PeriodSwitch'
import type { ReportPeriod } from './PeriodSwitch'
import { Figure, Row } from './reportParts'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const KIND_LABELS: Record<string, string> = { dues: 'Dues', project: 'Project giving', other: 'Other income' }

/** One department, one quarter: the whole picture on a single page, to read, print or open in Excel. */
export function QuarterReportPage({ deptId }: { deptId: string }) {
  const current = currentQuarterId()
  const thisMonth = monthId(lusakaToday())
  const [period, setPeriod] = useState<ReportPeriod>('quarter')
  const [quarter, setQuarter] = useState(current)
  const [month, setMonth] = useState(thisMonth)
  const q = useQuery({ queryKey: ['quarter-report', deptId, quarter], queryFn: () => getQuarterReport(deptId, quarter), enabled: period === 'quarter' })
  const mq = useQuery({ queryKey: ['month-statement', deptId, month], queryFn: () => getMonthStatement(deptId, month), enabled: period === 'month' })
  const depts = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const deptName = depts.data?.find((d) => d.id === deptId)?.name ?? 'Department'
  const r = q.data
  const mr = mq.data
  const fileBase = deptName.replace(/\s+/g, '-')

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-title font-semibold text-ink">{period === 'quarter' ? 'Quarterly report' : 'Monthly statement'}</h2>
          <p className="mt-1 text-muted">{deptName}, {period === 'quarter' ? quarterLabel(quarter) : formatMonth(`${month}-01`)}</p>
        </div>
        <div className="no-print flex flex-wrap items-end gap-2">
          <PeriodSwitch period={period} onChange={setPeriod} />
          {period === 'quarter' ? (
            <Select
              label="Quarter"
              value={quarter}
              onChange={(e) => setQuarter(e.target.value)}
              options={recentQuarters(current, 6).map((id) => ({ value: id, label: quarterLabel(id) }))}
            />
          ) : (
            <Select
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              options={recentMonths(thisMonth, 12).map((id) => ({ value: id, label: formatMonth(`${id}-01`) }))}
            />
          )}
          {period === 'quarter' ? (
            <Button variant="secondary" size="lg" disabled={!r} onClick={() => r && downloadCsv(`report-${fileBase}-${quarter}.csv`, quarterReportRows(r, deptName))}>
              Download for Excel
            </Button>
          ) : (
            <Button variant="secondary" size="lg" disabled={!mr} onClick={() => mr && downloadCsv(`statement-${fileBase}-${month}.csv`, monthStatementRows(mr, deptName))}>
              Download for Excel
            </Button>
          )}
          <Button variant="secondary" size="lg" disabled={period === 'quarter' ? !r : !mr} onClick={() => window.print()}>Print</Button>
        </div>
      </div>

      {period === 'month' ? (
        mq.isPending ? (
          <SkeletonRows rows={4} />
        ) : mq.isError ? (
          <div>
            <p role="alert" className="text-body text-bad">{errText(mq.error)}</p>
            <Button variant="secondary" size="lg" className="mt-3" onClick={() => void mq.refetch()}>Try again</Button>
          </div>
        ) : mr && <MonthStatementView r={mr} />
      ) : q.isPending ? (
        <SkeletonRows rows={4} />
      ) : q.isError ? (
        <div>
          <p role="alert" className="text-body text-bad">{errText(q.error)}</p>
          <Button variant="secondary" size="lg" className="mt-3" onClick={() => void q.refetch()}>Try again</Button>
        </div>
      ) : r && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" aria-label="Summary">
            <Figure label="Money in" value={formatKwacha(r.income.totalNgwee)} />
            <Figure label="Spent" value={formatKwacha(r.spending.paidNgwee)} />
            <Figure label="Sent to central" value={formatKwacha(r.remittances.declaredNgwee)} />
            <Figure label="Cash you should hold" value={r.position ? formatKwacha(r.position.cashOnHandNgwee) : formatKwacha(0)} tone={r.position && r.position.cashOnHandNgwee < 0 ? 'bad' : undefined} />
          </div>

          <Card>
            <CardHeader><h3 className="font-semibold text-ink">Money in</h3></CardHeader>
            <CardBody>
              {r.income.byMonth.length === 0 ? <p className="text-body text-muted">Nothing was collected in this quarter.</p> : (
                <ul className="divide-y divide-line">
                  {r.income.byMonth.map((m) => <Row key={m.month} left={formatMonth(`${m.month}-01`)} right={formatKwacha(m.ngwee)} />)}
                  <Row bold left="Total" right={formatKwacha(r.income.totalNgwee)} />
                  {r.income.byKind.length > 1 && r.income.byKind.map((k) => <Row key={k.kind} left={KIND_LABELS[k.kind] ?? k.kind} right={formatKwacha(k.ngwee)} />)}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h3 className="font-semibold text-ink">Budget against what was spent</h3></CardHeader>
            <CardBody>
              {!r.budget ? <p className="text-body text-muted">There is no approved budget for this quarter.</p> : (
                <>
                  <ul className="divide-y divide-line">
                    {r.budget.lines.map((l, idx) => {
                      const used = l.actualNgwee + l.committedNgwee
                      const over = l.remainingNgwee < 0
                      return (
                        <li key={`${idx}-${l.description}`} className="py-2">
                          <div className="flex items-start justify-between gap-3">
                            <span className="min-w-0 text-ink">{l.description}<span className="block text-body text-muted">{l.categoryName}</span></span>
                            <span className="shrink-0 text-right text-ink">{formatKwacha(l.actualNgwee)} <span className="text-muted">of {formatKwacha(l.budgetedNgwee)}</span></span>
                          </div>
                          <div className="mt-1 h-2 max-w-sm overflow-hidden rounded-full bg-neutral-tint" role="img" aria-label={`${percent(used, l.budgetedNgwee)}% used`}>
                            <div className={`h-full ${over ? 'bg-bad' : 'bg-brand'}`} style={{ width: `${percent(used, l.budgetedNgwee)}%` }} />
                          </div>
                          <p className={`mt-1 text-micro ${over ? 'text-bad' : 'text-muted'}`}>
                            {l.committedNgwee !== 0 ? `${formatKwacha(l.committedNgwee)} waiting or approved, ` : ''}
                            {over ? `over by ${formatKwacha(-l.remainingNgwee)}` : `${formatKwacha(l.remainingNgwee)} left`}
                          </p>
                        </li>
                      )
                    })}
                  </ul>
                  <ul className="mt-2 border-t border-line">
                    <Row bold left="Budgeted" right={formatKwacha(r.budget.budgetedNgwee)} />
                    <Row bold left="Spent" right={formatKwacha(r.budget.actualNgwee)} />
                  </ul>
                </>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h3 className="font-semibold text-ink">Spending by category</h3></CardHeader>
            <CardBody>
              {r.spending.byCategory.length === 0 ? <p className="text-body text-muted">Nothing was paid out in this quarter.</p> : (
                <ul className="divide-y divide-line">
                  {r.spending.byCategory.map((c) => <Row key={c.category} left={c.category} right={formatKwacha(c.ngwee)} />)}
                  <Row bold left="Total paid" right={formatKwacha(r.spending.paidNgwee)} />
                  {r.spending.unbudgetedNgwee !== 0 && <Row left="Of which not in the budget" right={formatKwacha(r.spending.unbudgetedNgwee)} />}
                </ul>
              )}
              {r.spending.waitingNgwee !== 0 && <p className="mt-2 text-body text-muted">{formatKwacha(r.spending.waitingNgwee)} more is waiting for approval or payment.</p>}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h3 className="font-semibold text-ink">Money sent to central treasury</h3></CardHeader>
            <CardBody>
              {r.remittances.items.length === 0 ? <p className="text-body text-muted">Nothing was sent in this quarter.</p> : (
                <ul className="divide-y divide-line">
                  {r.remittances.items.map((m, i) => (
                    <Row
                      key={i}
                      left={`${formatDay(m.movedOn)} · ${METHOD_LABELS[m.method as PaymentMethod] ?? m.method}`}
                      sub={`${MOVEMENT_STATUS_LABELS[m.status as MovementStatus] ?? m.status}${m.confirmedNgwee > 0 ? `, confirmed ${formatKwacha(m.confirmedNgwee)}` : ''}`}
                      right={formatKwacha(m.declaredNgwee)}
                    />
                  ))}
                  <Row bold left="Sent" right={formatKwacha(r.remittances.declaredNgwee)} />
                  <Row bold left="Confirmed received" right={formatKwacha(r.remittances.confirmedNgwee)} />
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><h3 className="font-semibold text-ink">Dues owed right now</h3></CardHeader>
            <CardBody>
              {r.arrears.members === 0 ? <p className="text-body text-muted">Nobody is behind on dues.</p> : (
                <p className="text-ink">{r.arrears.members} {r.arrears.members === 1 ? 'member owes' : 'members owe'} {formatKwacha(r.arrears.totalNgwee)} in total.</p>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  )
}
